import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Save, Sparkles, Target, Users, MessageSquare, 
  Settings, Play, AlertCircle, Check, Plus, Trash2, Clock, Calendar,
  Globe, Upload, Search, Filter, X, ChevronDown, Eye
} from 'lucide-react';
import { campaignService, instagramAccountService, leadsListService, campaignMessageService, campaignAccountService, campaignLeadsListService } from '../lib/supabaseServices';
import { MessageSequencesFull } from '../components/MessageSequencesFull';
import { ACCENT_COLOR_HEX } from '../lib/theme';
import LeadsImportModal from '../components/LeadsImportModal';

const CampaignCreate = ({ darkMode }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLeadsImport, setShowLeadsImport] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [availableLeadsLists, setAvailableLeadsLists] = useState([]);
  const [errors, setErrors] = useState({});
  
  const [campaignData, setCampaignData] = useState({
    // Basic Info
    name: '',
    description: '',
    
    // Leads
    selectedLeadsLists: [],
    
    // Messages (will be initialized by MessageSequencesFull)
    messageSequences: null,
    messageSettings: {
      useAIOptimization: false
    },
    
    // Accounts
    selectedAccounts: [],
    
    // Scheduling
    send_time_start: '09:00',
    send_time_end: '17:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    exclude_weekends: false,
    exclude_holidays: false,
    daily_message_min: 20,
    daily_message_max: 50
  });

  // Load accounts and leads lists on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [accounts, lists] = await Promise.all([
        instagramAccountService.getAccounts(),
        leadsListService.getLeadsLists()
      ]);
      setAvailableAccounts(accounts);
      setAvailableLeadsLists(lists);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  const steps = [
    { id: 'info', title: 'Campaign Info', icon: Target },
    { id: 'leads', title: 'Select Leads', icon: Users },
    { id: 'messages', title: 'Create Messages', icon: MessageSquare },
    { id: 'accounts', title: 'Instagram Accounts', icon: Users },
    { id: 'schedule', title: 'Schedule & Launch', icon: Calendar }
  ];

  const validateStep = (stepIndex) => {
    const newErrors = {};
    
    switch (stepIndex) {
      case 0: // Basic Info
        if (!campaignData.name.trim()) newErrors.name = 'Campaign name is required';
        if (!campaignData.description.trim()) newErrors.description = 'Description is required';
        break;
      case 1: // Leads
        if (campaignData.selectedLeadsLists.length === 0) newErrors.leads = 'Select at least one leads list';
        break;
      case 2: // Messages
        if (!campaignData.messageSequences?.some(s => s.enabled && s.opener?.text?.trim())) {
          newErrors.messages = 'At least one message sequence with an opener is required';
        }
        break;
      case 3: // Accounts
        if (campaignData.selectedAccounts.length === 0) newErrors.accounts = 'Select at least one Instagram account';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    try {
      setIsSubmitting(true);
      
      // Create the campaign
      const campaign = await campaignService.createCampaign({
        name: campaignData.name,
        description: campaignData.description,
        daily_message_min: campaignData.daily_message_min,
        daily_message_max: campaignData.daily_message_max,
        send_time_start: campaignData.send_time_start + ':00',
        send_time_end: campaignData.send_time_end + ':00',
        timezone: campaignData.timezone,
        exclude_weekends: campaignData.exclude_weekends,
        exclude_holidays: campaignData.exclude_holidays,
        status: 'active'
      });

      // Save messages
      if (campaignData.messageSequences) {
        const messagesToSave = {};
        
        // Process each enabled sequence. Each sequence is an A/B opener variant
        // (a, b, c, …) with its own unlimited follow-up chain. Naming MUST match
        // what the task-completion edge function chains on:
        //   opener_a → followup1_a → followup2_a → …  (no underscore before the number)
        campaignData.messageSequences.filter(s => s.enabled).forEach((sequence, seqIndex) => {
          const variant = String.fromCharCode(65 + seqIndex).toLowerCase(); // 'a','b','c',…

          // Opener for this sequence
          if (sequence.opener && sequence.opener.text) {
            messagesToSave[`opener_${variant}`] = {
              text: sequence.opener.text,
              enabled: true
            };
          }

          // Unlimited follow-ups for THIS sequence, variant-suffixed so the
          // backend chains them per-sequence (followup1_a, followup2_a, …).
          sequence.followups?.filter(f => f.enabled && f.text).forEach((followup, index) => {
            messagesToSave[`followup${index + 1}_${variant}`] = {
              text: followup.text,
              enabled: true,
              delay_days: followup.delay_days
            };
          });
        });
        
        if (Object.keys(messagesToSave).length > 0) {
          await campaignMessageService.createCampaignMessages(campaign.id, messagesToSave);
        }
      }
      
      // Associate accounts
      for (const accountId of campaignData.selectedAccounts) {
        await campaignAccountService.addAccountToCampaign(campaign.id, accountId);
      }
      
      // Associate leads lists
      if (campaignData.selectedLeadsLists.length > 0) {
        const leadsListIds = campaignData.selectedLeadsLists.map(list => list.id);
        await campaignLeadsListService.addLeadsListsToCampaign(campaign.id, leadsListIds);
      }
      
      // Navigate to campaigns page
      navigate('/dashboard/campaigns');
    } catch (error) {
      console.error('Failed to create campaign:', error);
      setErrors({ submit: 'Failed to create campaign. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <StepBasicInfo data={campaignData} onChange={setCampaignData} errors={errors} darkMode={darkMode} />;
      case 1:
        return <StepLeads 
          data={campaignData} 
          onChange={setCampaignData} 
          availableLists={availableLeadsLists}
          onShowImport={() => setShowLeadsImport(true)}
          errors={errors} 
          darkMode={darkMode} 
        />;
      case 2:
        return <MessageSequencesFull data={campaignData} onDataChange={setCampaignData} darkMode={darkMode} />;
      case 3:
        return <StepAccounts 
          data={campaignData} 
          onChange={setCampaignData} 
          availableAccounts={availableAccounts}
          errors={errors} 
          darkMode={darkMode} 
        />;
      case 4:
        return <StepSchedule data={campaignData} onChange={setCampaignData} onSubmit={handleSubmit} isSubmitting={isSubmitting} darkMode={darkMode} />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen"
    >
      {/* Header */}
      <div className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard/campaigns')}
                className={`p-2 rounded transition-colors ${
                  darkMode ? 'hover:bg-elevated text-secondary' : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Create New Campaign
                </h1>
                <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                  {steps[currentStep].title}
                </p>
              </div>
            </div>
            
            {/* Step Indicators */}
            <div className="hidden md:flex items-center space-x-2">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;
                
                return (
                  <React.Fragment key={step.id}>
                    <button
                      onClick={() => index < currentStep && setCurrentStep(index)}
                      disabled={index > currentStep}
                      className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                        isActive
                          ? darkMode
                            ? 'bg-accent/20 text-accent border border-accent/30'
                            : 'bg-accent/10 text-accent border border-accent/20'
                          : isCompleted
                          ? darkMode
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 cursor-pointer'
                            : 'bg-green-100 text-green-600 border border-green-200 cursor-pointer'
                          : darkMode
                          ? 'text-muted'
                          : 'text-gray-400'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Icon className="w-4 h-4" />
                      )}
                      <span className="text-sm font-medium">{step.title}</span>
                    </button>
                    {index < steps.length - 1 && (
                      <div className={`w-8 h-0.5 ${
                        index < currentStep
                          ? darkMode ? 'bg-emerald-500' : 'bg-green-500'
                          : darkMode ? 'bg-border' : 'bg-gray-300'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
        
        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex items-center space-x-2 px-4 py-2 rounded transition-colors ${
              currentStep === 0
                ? darkMode ? 'bg-surface text-muted cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : darkMode ? 'bg-elevated text-primary hover:bg-elevated/80' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          
          {currentStep < steps.length - 1 ? (
            <button
              onClick={handleNext}
              className={`flex items-center space-x-2 px-6 py-2 rounded font-medium transition-colors ${
                darkMode
                  ? 'bg-gradient-to-r from-accent to-accent-hover text-white hover:shadow-lg'
                  : 'bg-gradient-to-r from-accent to-accent-hover text-white hover:shadow-lg'
              }`}
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`flex items-center space-x-2 px-6 py-2 rounded font-medium transition-colors ${
                isSubmitting
                  ? darkMode ? 'bg-elevated text-muted cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : darkMode
                  ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg'
                  : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span>Launch Campaign</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Leads Import Modal */}
      {showLeadsImport && (
        <LeadsImportModal
          isOpen={showLeadsImport}
          onClose={() => setShowLeadsImport(false)}
          darkMode={darkMode}
          onSuccess={(leadsList) => {
            setCampaignData(prev => ({
              ...prev,
              selectedLeadsLists: [...prev.selectedLeadsLists, leadsList]
            }));
            setAvailableLeadsLists(prev => [...prev, leadsList]);
            setShowLeadsImport(false);
          }}
        />
      )}
    </motion.div>
  );
};

// Step Components
const StepBasicInfo = ({ data, onChange, errors, darkMode }) => (
  <div className="space-y-6">
    <div>
      <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
        Let's start with the basics
      </h2>
      <p className={`${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
        Give your campaign a memorable name and description
      </p>
    </div>
    
    <div className="grid grid-cols-1 gap-6">
      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
          Campaign Name *
        </label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          placeholder="e.g., Summer Outreach 2024"
          className={`w-full px-4 py-3 border rounded focus:ring-2 focus:border-transparent ${
            errors.name
              ? 'border-red-500'
              : darkMode 
              ? 'bg-surface border-border text-primary placeholder-secondary focus:ring-accent' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
          }`}
        />
        {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
      </div>
      
      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
          Description *
        </label>
        <textarea
          value={data.description}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          placeholder="Describe your campaign goals and target audience..."
          rows={4}
          className={`w-full px-4 py-3 border rounded focus:ring-2 focus:border-transparent ${
            errors.description
              ? 'border-red-500'
              : darkMode 
              ? 'bg-surface border-border text-primary placeholder-secondary focus:ring-accent' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
          }`}
        />
        {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
      </div>
    </div>
  </div>
);

const StepLeads = ({ data, onChange, availableLists, onShowImport, errors, darkMode }) => (
  <div className="space-y-6">
    <div>
      <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
        Select Your Leads
      </h2>
      <p className={`${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
        Choose existing lists or import new leads for your campaign
      </p>
    </div>
    
    {errors.leads && (
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded">
        <p className="text-red-500">{errors.leads}</p>
      </div>
    )}
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {availableLists.map(list => {
        const isSelected = data.selectedLeadsLists.some(l => l.id === list.id);
        return (
          <button
            key={list.id}
            onClick={() => {
              if (isSelected) {
                onChange({
                  ...data,
                  selectedLeadsLists: data.selectedLeadsLists.filter(l => l.id !== list.id)
                });
              } else {
                onChange({
                  ...data,
                  selectedLeadsLists: [...data.selectedLeadsLists, list]
                });
              }
            }}
            className={`p-4 border-2 rounded text-left transition-all ${
              isSelected
                ? darkMode
                  ? 'border-accent bg-accent/10'
                  : 'border-accent bg-accent/10'
                : darkMode
                  ? 'border-slate-700 hover:border-slate-600'
                  : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <Users className={`w-5 h-5 ${isSelected ? 'text-accent' : darkMode ? 'text-secondary' : 'text-gray-400'}`} />
              {isSelected && <Check className="w-5 h-5 text-accent" />}
            </div>
            <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {list.name}
            </h3>
            <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'} mt-1`}>
              {list.total_leads || 0} leads
            </p>
          </button>
        );
      })}
      
      <button
        onClick={onShowImport}
        className={`p-4 border-2 border-dashed rounded flex flex-col items-center justify-center space-y-2 transition-colors ${
          darkMode
            ? 'border-border text-secondary hover:border-accent hover:text-accent'
            : 'border-gray-300 text-gray-500 hover:border-accent hover:text-accent'
        }`}
      >
        <Upload className="w-8 h-8" />
        <span className="font-medium">Import New Leads</span>
      </button>
    </div>
    
    {data.selectedLeadsLists.length > 0 && (
      <div className={`p-4 ${darkMode ? 'bg-surface' : 'bg-gray-50'} rounded`}>
        <h4 className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Selected Lists ({data.selectedLeadsLists.length})
        </h4>
        <div className="flex flex-wrap gap-2">
          {data.selectedLeadsLists.map(list => (
            <span
              key={list.id}
              className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                darkMode ? 'bg-accent/20 text-accent' : 'bg-accent/10 text-accent'
              }`}
            >
              <span>{list.name}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({
                    ...data,
                    selectedLeadsLists: data.selectedLeadsLists.filter(l => l.id !== list.id)
                  });
                }}
                className="hover:opacity-70"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);

const StepAccounts = ({ data, onChange, availableAccounts, errors, darkMode }) => (
  <div className="space-y-6">
    <div>
      <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
        Select Instagram Accounts
      </h2>
      <p className={`${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
        Choose which accounts will send messages for this campaign
      </p>
    </div>
    
    {errors.accounts && (
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded">
        <p className="text-red-500">{errors.accounts}</p>
      </div>
    )}
    
    <div className="space-y-3">
      {availableAccounts.map(account => {
        const isSelected = data.selectedAccounts.includes(account.id);
        return (
          <button
            key={account.id}
            onClick={() => {
              if (isSelected) {
                onChange({
                  ...data,
                  selectedAccounts: data.selectedAccounts.filter(id => id !== account.id)
                });
              } else {
                onChange({
                  ...data,
                  selectedAccounts: [...data.selectedAccounts, account.id]
                });
              }
            }}
            className={`w-full p-4 border-2 rounded transition-all ${
              isSelected
                ? darkMode
                  ? 'border-accent bg-accent/10'
                  : 'border-accent bg-accent/10'
                : darkMode
                  ? 'border-slate-700 hover:border-slate-600'
                  : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img
                  src={account.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(account.username)}&background=${ACCENT_COLOR_HEX}&color=ffffff&size=40`}
                  alt={account.username}
                  className="w-10 h-10 rounded-full"
                />
                <div className="text-left">
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    @{account.username}
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                    {account.messages_sent_today || 0} / 50 messages today
                  </p>
                </div>
              </div>
              {isSelected && <Check className="w-5 h-5 text-accent" />}
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const StepSchedule = ({ data, onChange, onSubmit, isSubmitting, darkMode }) => (
  <div className="space-y-6">
    <div>
      <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
        Schedule Your Campaign
      </h2>
      <p className={`${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
        Configure when and how your messages will be sent
      </p>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
          Daily Message Range
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="number"
            min="1"
            max="50"
            value={data.daily_message_min}
            onChange={(e) => onChange({ ...data, daily_message_min: parseInt(e.target.value) })}
            className={`w-20 px-3 py-2 border rounded ${
              darkMode 
                ? 'bg-surface border-border text-primary' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
          <span className={darkMode ? 'text-secondary' : 'text-gray-600'}>to</span>
          <input
            type="number"
            min="1"
            max="50"
            value={data.daily_message_max}
            onChange={(e) => onChange({ ...data, daily_message_max: parseInt(e.target.value) })}
            className={`w-20 px-3 py-2 border rounded ${
              darkMode 
                ? 'bg-surface border-border text-primary' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
          <span className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>messages per day</span>
        </div>
      </div>
      
      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
          Operating Hours
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="time"
            value={data.send_time_start}
            onChange={(e) => onChange({ ...data, send_time_start: e.target.value })}
            className={`px-3 py-2 border rounded ${
              darkMode 
                ? 'bg-surface border-border text-primary' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
          <span className={darkMode ? 'text-secondary' : 'text-gray-600'}>to</span>
          <input
            type="time"
            value={data.send_time_end}
            onChange={(e) => onChange({ ...data, send_time_end: e.target.value })}
            className={`px-3 py-2 border rounded ${
              darkMode 
                ? 'bg-surface border-border text-primary' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>
      </div>
      
      <div className={`col-span-full p-4 ${darkMode ? 'bg-surface' : 'bg-gray-50'} rounded space-y-3`}>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data.exclude_weekends}
            onChange={(e) => onChange({ ...data, exclude_weekends: e.target.checked })}
            className="rounded"
          />
          <span className={`text-sm ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
            Exclude weekends
          </span>
        </label>
        
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data.exclude_holidays}
            onChange={(e) => onChange({ ...data, exclude_holidays: e.target.checked })}
            className="rounded"
          />
          <span className={`text-sm ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
            Exclude holidays
          </span>
        </label>
      </div>
    </div>
    
    {/* Campaign Summary */}
    <div className={`p-6 ${darkMode ? 'bg-surface border-border' : 'bg-gray-50 border-gray-200'} border rounded`}>
      <h3 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Campaign Summary
      </h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className={darkMode ? 'text-secondary' : 'text-gray-600'}>Campaign Name:</span>
          <span className={darkMode ? 'text-white' : 'text-gray-900'}>{data.name || 'Not set'}</span>
        </div>
        <div className="flex justify-between">
          <span className={darkMode ? 'text-secondary' : 'text-gray-600'}>Leads Lists:</span>
          <span className={darkMode ? 'text-white' : 'text-gray-900'}>{data.selectedLeadsLists.length} selected</span>
        </div>
        <div className="flex justify-between">
          <span className={darkMode ? 'text-secondary' : 'text-gray-600'}>Message Sequences:</span>
          <span className={darkMode ? 'text-white' : 'text-gray-900'}>
            {data.messageSequences?.filter(s => s.enabled).length || 0} sequences with follow-ups
          </span>
        </div>
        <div className="flex justify-between">
          <span className={darkMode ? 'text-secondary' : 'text-gray-600'}>Instagram Accounts:</span>
          <span className={darkMode ? 'text-white' : 'text-gray-900'}>{data.selectedAccounts.length} selected</span>
        </div>
        <div className="flex justify-between">
          <span className={darkMode ? 'text-slate-400' : 'text-gray-600'}>Daily Messages:</span>
          <span className={darkMode ? 'text-white' : 'text-gray-900'}>{data.daily_message_min} - {data.daily_message_max}</span>
        </div>
      </div>
    </div>
  </div>
);

export default CampaignCreate;